import { useEffect, useState } from 'preact/hooks'
import { clsx } from 'clsx'
import {
	Combobox,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
	Field
} from '@headlessui/react'

import { CheckIcon, Typography } from 'components/UI/atoms'

type Props = {
	error?: string
	hasError?: boolean
	helperText?: string
	label: string
	subLabel?: string
	placeholder?: string
	setSelected: (
		value: {
			id: string | null
			name: string
		} | null
	) => void
	options: { name: string; id: string }[]
	value: { id: string | null; name: string } | null
}

const ComboBoxAutocomplete = ({
	label,
	subLabel,
	error,
	hasError,
	helperText,
	placeholder,
	options,
	setSelected,
	value
}: Props) => {
	const [query, setQuery] = useState('')

	const filteredOptions =
		query === ''
			? options
			: options.filter(option =>
					option.name.toLowerCase().includes(query.toLowerCase())
				)

	useEffect(() => {
		if (query) {
			const selectedOption = options.find(option => option.name === query)

			if (selectedOption) {
				setSelected(selectedOption)
			} else {
				setSelected({ id: null, name: query })
			}
		}
	}, [options, query, setSelected])

	return (
		<Field>
			<Typography
				class='block'
				color='text-black-100'
				text={label}
				variant='label3'
			>
				{subLabel && (
					<Typography
						class='ml-1'
						color='text-black-100'
						text={subLabel}
						variant='label3'
						weight='font-normal'
					/>
				)}
			</Typography>

			<Combobox
				onChange={setSelected}
				onClose={() => setQuery('')}
				value={value}
				immediate
			>
				<div className='relative mt-2'>
					<ComboboxInput
						autocomplete='off'
						className={clsx(
							'h-10 w-full rounded border border-black-500 bg-black-800 py-2.5 pl-4 pr-2.5 font-inter text-sm font-normal text-customWhite placeholder:font-light placeholder:text-brightText focus:border-white focus:ring-white sm:text-sm sm:leading-6'
						)}
						displayValue={option => option?.name}
						onChange={event => setQuery(event.target.value)}
						placeholder={placeholder}
					/>
				</div>

				<ComboboxOptions
					anchor='bottom'
					className={clsx(
						'z-50 mt-1 max-h-72 w-[var(--input-width)] rounded-lg border border-gray-200 bg-black-850 shadow-xl transition duration-100 ease-in [--anchor-gap:var(--spacing-1)] empty:invisible data-[leave]:data-[closed]:opacity-0'
					)}
					transition
				>
					{filteredOptions.map(option => (
						<ComboboxOption
							className='group flex cursor-default select-none items-center gap-2 rounded-lg px-3 py-1.5 data-[focus]:bg-black-800 group-data-[selected]:bg-black-800'
							key={option.id}
							value={option}
						>
							<CheckIcon className='invisible size-4 fill-white group-data-[selected]:visible' />
							<div class='flex w-full items-center justify-between'>
								<Typography
									text={option.name}
									variant='span2'
									weight='font-normal'
								/>
								<Typography
									color='text-black-200'
									text={option.id}
									variant='span4'
								/>
							</div>
						</ComboboxOption>
					))}
				</ComboboxOptions>
			</Combobox>

			{!hasError && helperText && (
				<Typography
					color='text-black-200'
					text={helperText}
					variant='label3'
					weight='font-normal'
				/>
			)}
			{hasError && (
				<Typography
					color='text-error'
					text={error!}
					variant='label3'
					weight='font-normal'
				/>
			)}
		</Field>
	)
}

export { ComboBoxAutocomplete }
